
const khReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!customElements.get('kienzle-reveal')) {
  class KienzleReveal extends HTMLElement {
    connectedCallback() {
      const items = [...this.querySelectorAll('[data-kh-reveal], [data-kh-count]')];

      if (khReducedMotion() || !('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-in'));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            if (entry.target.hasAttribute('data-kh-count')) this.countUp(entry.target);
            this.observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );

      items.forEach((item) => this.observer.observe(item));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    countUp(element) {
      const target = parseInt(element.dataset.khCount, 10);
      if (!target) return;
      const duration = 1600;
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        element.textContent = Math.round(target * eased).toLocaleString('de-DE');
        if (progress < 1) requestAnimationFrame(step);
      };
      element.textContent = '0';
      requestAnimationFrame(step);
    }
  }

  customElements.define('kienzle-reveal', KienzleReveal);
}

if (!customElements.get('kienzle-chronicle')) {
  class KienzleChronicle extends HTMLElement {
    connectedCallback() {
      this.chapters = [...this.querySelectorAll('[data-kh-chapter]')];
      this.links = [...this.querySelectorAll('[data-kh-nav] a')];
      this.nav = this.querySelector('[data-kh-nav]');
      this.year = this.querySelector('[data-kh-year]');
      this.railTitle = this.querySelector('[data-kh-title]');
      this.current = -1;

      if (!this.chapters.length) return;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    update() {
      const line = window.innerHeight * 0.45;
      let index = 0;
      this.chapters.forEach((chapter, position) => {
        if (chapter.getBoundingClientRect().top <= line) index = position;
      });

      if (index !== this.current) {
        this.current = index;
        this.activate(index);
      }

      this.follow(index, line);
    }

    follow(index, line) {
      const link = this.links[index];
      if (!this.nav || !link) return;

      const box = this.chapters[index].getBoundingClientRect();
      const share = Math.min(Math.max((line - box.top) / box.height, 0), 1);
      const next = this.links[index + 1];
      const middle = (entry) => {
        if (this.horizontal()) return entry.offsetLeft + entry.offsetWidth / 2;
        const style = getComputedStyle(entry);
        return entry.offsetTop + parseFloat(style.paddingTop) + parseFloat(style.lineHeight) / 2;
      };

      const from = middle(link);
      const to = next ? middle(next) : from;
      this.nav.style.setProperty('--kh-progress', `${(from + (to - from) * share).toFixed(1)}px`);
    }

    horizontal() {
      return window.matchMedia('(max-width: 959px)').matches;
    }

    activate(index) {
      const chapter = this.chapters[index];

      this.links.forEach((link, position) => {
        link.classList.toggle('is-done', position < index);
        link.classList.toggle('is-on', position === index);
        if (position === index) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
      });

      if (this.year) {
        this.year.textContent = chapter.dataset.year;
        this.year.classList.toggle('is-long', chapter.dataset.year.length > 4);
        this.year.classList.remove('is-flip');
        void this.year.offsetWidth;
        if (!khReducedMotion()) this.year.classList.add('is-flip');
      }
      if (this.railTitle) this.railTitle.textContent = chapter.dataset.title;

      const link = this.links[index];
      if (this.nav && link) {
        const behavior = khReducedMotion() ? 'auto' : 'smooth';
        if (!this.horizontal()) {
          const middle = link.offsetTop + link.offsetHeight / 2;
          this.nav.scrollTo({ top: middle - this.nav.clientHeight / 2, behavior });
        } else {
          const middle = link.offsetLeft + link.offsetWidth / 2;
          this.nav.scrollTo({ left: middle - this.nav.clientWidth / 2, behavior });
        }
      }
    }
  }

  customElements.define('kienzle-chronicle', KienzleChronicle);
}

if (!customElements.get('kienzle-chapter-video')) {
  class KienzleChapterVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('.kh-video__toggle');
      if (!this.video || !this.button) return;

      this.button.addEventListener('click', () => this.toggle());
      this.video.addEventListener('click', () => this.toggle());
      this.video.addEventListener('play', () => this.sync(true));
      this.video.addEventListener('pause', () => this.sync(false));
      this.video.addEventListener('ended', () => this.sync(false));
    }

    toggle() {
      if (this.video.paused) {
        this.video.play();
      } else {
        this.video.pause();
      }
    }

    sync(playing) {
      this.toggleAttribute('playing', playing);
      this.button.setAttribute('aria-pressed', playing ? 'true' : 'false');
    }
  }

  customElements.define('kienzle-chapter-video', KienzleChapterVideo);
}
